import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import ProsecutorApi, { FullProsecutorDto } from '../../api/ProsecutorApi';
import './ProsecutorSelect.scss';

interface ProsecutorSelectProps {
  value?: FullProsecutorDto | null;
  onChange: (prosecutor: FullProsecutorDto | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  courtFilter?: string; // Optional filter by court name (for backwards compatibility)
  courtGuidFilter?: string; // Optional filter by court guid (more precise)
}

const ProsecutorSelect: React.FC<ProsecutorSelectProps> = ({
  value,
  onChange,
  label = "Select Prosecutor",
  placeholder = "Search for prosecutors...",
  disabled = false,
  error = false,
  helperText,
  required = false,
  courtFilter,
  courtGuidFilter,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FullProsecutorDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  /**
   * Recording a prosecutor nobody has entered yet (Lloyd, 16 Sep: "you should be able to add the
   * prosecutor's name, if not to the client's case, at least to the court, though adding it once
   * should add it to both").
   *
   * A prosecutor already belongs to a court, so creating one here and selecting it does serve both.
   * Before this, the only way in was a separate screen, and staff handed a name, an email and a
   * phone had no way to use them from the case they were working.
   */
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const closeAdd = () => {
    setAddOpen(false);
    setAddName('');
    setAddEmail('');
    setAddPhone('');
    setAddError(null);
  };

  const saveNewProsecutor = async () => {
    if (!courtGuidFilter) return;
    setAddSaving(true);
    setAddError(null);
    try {
      const created = await ProsecutorApi.createProsecutor({
        Prosecutor_Name: addName.trim(),
        Prosecutor_Email: addEmail.trim(),
        Prosecutor_Phone: addPhone.trim() || null,
        Court_guid: courtGuidFilter,
      });
      // Selected straight away. Creating one and then having to search for it is the round trip
      // this exists to remove.
      onChange(created);
      closeAdd();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add the prosecutor');
    } finally {
      setAddSaving(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchTerm: string) => {
      // If no search term and no court filter, clear options
      if (!searchTerm && !courtFilter && !courtGuidFilter) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const searchParams: any = {
          size: 50, // Limit results
          Prosecutor_Active: true, // Only show active prosecutors
        };

        if (searchTerm) {
          searchParams.search = searchTerm;
        }

        // Prefer Court_guid for more precise filtering, fall back to Court_Name
        if (courtGuidFilter) {
          searchParams.Court_guid = courtGuidFilter;
        } else if (courtFilter) {
          searchParams.Court_Name = courtFilter;
        }

        const response = await ProsecutorApi.searchProsecutors(searchParams);
        setOptions(response.prosecutors);
      } catch (error) {
        console.error('Error searching prosecutors:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [courtFilter, courtGuidFilter]
  );

  // Effect to trigger search when input changes
  useEffect(() => {
    if (open) {
      debouncedSearch(inputValue);
    }
  }, [inputValue, debouncedSearch, open]);

  // Load initial options when component opens
  useEffect(() => {
    if (open && options.length === 0 && !inputValue) {
      debouncedSearch('');
    }
  }, [open, debouncedSearch, options.length, inputValue]);

  // Load prosecutors when court filter changes
  useEffect(() => {
    if ((courtGuidFilter || courtFilter) && open) {
      debouncedSearch(inputValue);
    }
  }, [courtGuidFilter, courtFilter, open, debouncedSearch, inputValue]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: FullProsecutorDto | null) => {
    onChange(newValue);
  };

  const formatOptionLabel = (option: FullProsecutorDto) => {
    return `${option.Prosecutor_Name} - ${option.court?.Court_Name || 'Unknown Court'}`;
  };

  const renderOption = (props: any, option: FullProsecutorDto) => (
    <Box component="li" {...props} key={option.Prosecutor_guid}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="body1" component="div">
          {option.Prosecutor_Name}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          {option.Prosecutor_Email}
        </Typography>
        {option.court && (
          <Chip
            label={option.court.Court_Name}
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );

  return (
    <div className="prosecutor-select">
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={formatOptionLabel}
        renderOption={renderOption}
        loading={loading}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => 
          option.Prosecutor_guid === value?.Prosecutor_guid
        }
        filterOptions={(options) => options} // Disable built-in filtering since we're doing server-side search
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={required}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.Prosecutor_guid}
              label={option.Prosecutor_Name}
              size="small"
            />
          ))
        }
        noOptionsText={
          inputValue || courtFilter || courtGuidFilter ? 
            "No prosecutors found" : 
            "Start typing to search for prosecutors"
        }
        loadingText="Searching prosecutors..."
      />

      {/* Only with a court chosen, because a prosecutor cannot exist without one. */}
      {courtGuidFilter && !disabled && (
        <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setAddOpen(true)}>
          Add a prosecutor to this court
        </Button>
      )}

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="xs" fullWidth>
        <DialogTitle>Add a prosecutor</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recorded against this case's court, so it is available on every case at that court.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={addPhone}
              onChange={(e) => setAddPhone(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveNewProsecutor}
            disabled={addSaving || !addName.trim() || !addEmail.trim()}
          >
            {addSaving ? 'Adding…' : 'Add and select'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProsecutorSelect;
